import { Controller } from "@hotwired/stimulus";

// Only http(s) URLs or app-relative paths may reach an href/Turbo.visit call —
// mirrors isSafeUrl() in discord_description_editor.js and #safeImgUrl() in
// message_editor_controller.js, the codebase's established guard against
// javascript:/data: URLs reaching the DOM via a server-supplied href.
function isSafeHref(url) {
  const str = String(url ?? "").trim();
  return /^https?:\/\//i.test(str) || str.startsWith("/");
}

const KONAMI_SEQUENCE = [
  "ArrowUp",
  "ArrowUp",
  "ArrowDown",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowLeft",
  "ArrowRight",
  "b",
  "a",
  "Enter",
];

export default class CommandPaletteController extends Controller {
  static targets = ["dialog", "input", "list"];
  static values = {
    items: Array,
    noResults: { type: String, default: "No results" },
    keyboardShortcuts: { type: String, default: "Keyboard Shortcuts" },
    openPalette: { type: String, default: "Open command palette" },
    searchUrl: { type: String, default: "" },
    suggestionsGroupLabel: { type: String, default: "Suggestions" },
    linksGroupLabel: { type: String, default: "Links" },
  };

  #leaderActive = false;
  #leaderTimer = null;
  #activeIndex = -1;
  #boundHandleKey = null;
  #konamiBuffer = [];
  #searchTimer = null;
  #searchRequestId = 0;

  connect() {
    this.#boundHandleKey = this.#handleKey.bind(this);
    window.addEventListener("keydown", this.#boundHandleKey);

    // Close on backdrop click (click on the <dialog> element itself, not its contents)
    this.dialogTarget.addEventListener("click", (e) => {
      if (e.target === this.dialogTarget) this.close();
    });

    this.inputTarget.setAttribute("role", "combobox");
    this.inputTarget.setAttribute("aria-haspopup", "listbox");
    this.inputTarget.setAttribute("aria-expanded", "false");
    this.inputTarget.setAttribute("aria-autocomplete", "list");
    this.inputTarget.setAttribute("aria-controls", "cmd-palette-listbox");
    this.listTarget.id = "cmd-palette-listbox";
    this.listTarget.setAttribute("role", "listbox");
  }

  disconnect() {
    window.removeEventListener("keydown", this.#boundHandleKey);
    clearTimeout(this.#leaderTimer);
    clearTimeout(this.#searchTimer);
  }

  // ── Keyboard handler (window-level) ──────────────────────────────────────

  #handleKey(event) {
    if ((event.metaKey || event.ctrlKey) && event.key === "k") {
      event.preventDefault();
      if (this.dialogTarget.open) {
        this.close();
      } else {
        this.open();
      }
      return;
    }

    if (this.dialogTarget.open) {
      this.#checkKonami(event);
      return;
    }

    this.#handleLeaderKey(event);
  }

  #checkKonami(event) {
    this.#konamiBuffer.push(event.key);
    if (this.#konamiBuffer.length > KONAMI_SEQUENCE.length) this.#konamiBuffer.shift();
    if (this.#konamiBuffer.join(",") === KONAMI_SEQUENCE.join(",")) {
      this.#konamiBuffer = [];
      event.preventDefault(); // stop Enter activating the focused button (e.g. the palette trigger)
      this.close();
      window.StreamCommand?.toast("🎮 Toasty!");
      return true;
    }
    return false;
  }

  #inputFocused() {
    const tag = document.activeElement?.tagName.toLowerCase();
    return (
      ["input", "textarea", "select"].includes(tag) || !!document.activeElement?.isContentEditable
    );
  }

  #noModifiers(event) {
    return !event.metaKey && !event.ctrlKey && !event.altKey;
  }

  #handleLeaderKey(event) {
    if (this.#inputFocused()) return;

    if (event.key === "?" && this.#noModifiers(event)) {
      event.preventDefault();
      document.getElementById("sc-shortcuts-dialog")?.showModal();
      return;
    }

    if (event.key === "g" && this.#noModifiers(event)) {
      this.#leaderActive = true;
      clearTimeout(this.#leaderTimer);
      this.#leaderTimer = setTimeout(() => {
        this.#leaderActive = false;
      }, 1500);
      return;
    }

    if (this.#leaderActive) {
      this.#leaderActive = false;
      clearTimeout(this.#leaderTimer);
      const item = this.itemsValue.find((i) => i.shortcut === `g ${event.key}`);
      if (item) {
        event.preventDefault();
        this.#navigate(item.href);
      }
    }
  }

  // ── Palette open / close ─────────────────────────────────────────────────

  open() {
    this.#activeIndex = -1;
    this.inputTarget.value = "";
    this.#renderItems(this.itemsValue.filter((i) => !i.exact_query));
    this.dialogTarget.showModal();
    this.inputTarget.setAttribute("aria-expanded", "true");
    // Defer focus so showModal() animation doesn't steal it
    requestAnimationFrame(() => this.inputTarget.focus());
  }

  close() {
    clearTimeout(this.#searchTimer);
    this.inputTarget.setAttribute("aria-expanded", "false");
    this.dialogTarget.close();
  }

  // ── Search filter ────────────────────────────────────────────────────────

  filter() {
    const q = this.inputTarget.value.trim().toLowerCase();
    const filtered = q
      ? this.itemsValue.filter((i) => {
          if (i.exact_query) return i.exact_query.toLowerCase() === q;
          return i.label.toLowerCase().includes(q) || (i.shortcut || "").includes(q);
        })
      : this.itemsValue.filter((i) => !i.exact_query);
    this.#activeIndex = -1;
    this.#renderItems(filtered);

    // Content search — debounced fetch when query is long enough
    clearTimeout(this.#searchTimer);
    if (this.searchUrlValue && q.length >= 2) {
      this.#searchTimer = setTimeout(() => this.#fetchSearchResults(q), 300);
    } else {
      this.#clearServerResults();
    }
  }

  // ── Content search (server) ──────────────────────────────────────────────

  async #fetchSearchResults(q) {
    const requestId = ++this.#searchRequestId;
    const url = new URL(this.searchUrlValue, window.location.origin);
    url.searchParams.set("q", q);
    try {
      const res = await fetch(url, {
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content ?? "",
        },
      });
      if (requestId !== this.#searchRequestId) return;
      if (!res.ok) return;
      const data = await res.json();
      if (requestId !== this.#searchRequestId) return;
      this.#appendServerResults(data);
    } catch {
      // Network errors are silent — palette still works with static results
    }
  }

  #appendServerResults(data) {
    this.#clearServerResults();

    const groups = [
      { key: "suggestions", label: this.suggestionsGroupLabelValue },
      { key: "links", label: this.linksGroupLabelValue },
    ];

    let anyResults = false;
    for (const { key, label } of groups) {
      const items = data[key];
      if (!items?.length) continue;
      anyResults = true;

      const sep = document.createElement("div");
      sep.className = "cmd-palette-server cmd-palette-group-sep";
      this.listTarget.appendChild(sep);

      const heading = document.createElement("p");
      heading.className = "cmd-palette-server cmd-palette-group-label";
      heading.textContent = label;
      this.listTarget.appendChild(heading);

      items.forEach((item, idx) => {
        if (!isSafeHref(item.href)) return;

        const el = document.createElement("a");
        el.href = item.href;
        el.dataset.href = item.href;
        el.dataset.action = "click->command-palette#closeOnItemClick";
        el.className = "cmd-palette-item cmd-palette-server";
        el.setAttribute("role", "option");
        el.setAttribute("aria-selected", "false");
        el.id = `cmd-palette-server-${key}-${idx}`;
        if (item.new_tab) {
          el.target = "_blank";
          el.rel = "noopener noreferrer";
        }

        const icon = document.createElement("i");
        icon.className = `${item.icon} cmd-palette-icon`;
        icon.setAttribute("aria-hidden", "true");

        const labelEl = document.createElement("span");
        labelEl.className = "cmd-palette-label";
        labelEl.textContent = item.label;

        el.appendChild(icon);
        el.appendChild(labelEl);
        this.listTarget.appendChild(el);
      });
    }

    // If the static list only showed "no results" and we now have server results,
    // remove the empty-state message.
    if (anyResults) {
      this.listTarget.querySelector(".cmd-palette-empty")?.remove();
    }
  }

  #clearServerResults() {
    this.listTarget.querySelectorAll(".cmd-palette-server").forEach((el) => el.remove());
  }

  // ── Keyboard navigation inside the list ─────────────────────────────────

  navigateList(event) {
    const items = Array.from(
      this.listTarget.querySelectorAll("[data-href], [data-palette-action]"),
    );
    if (!items.length) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      this.#activeIndex = Math.min(this.#activeIndex + 1, items.length - 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      this.#activeIndex = Math.max(this.#activeIndex - 1, 0);
    } else if (event.key === "Enter") {
      event.preventDefault();
      this.#handleEnterKey(items);
      return;
    } else {
      return;
    }

    items.forEach((el, i) => {
      const active = i === this.#activeIndex;
      el.classList.toggle("cmd-palette-active", active);
      el.setAttribute("aria-selected", String(active));
    });
    const activeEl = items[this.#activeIndex];
    activeEl?.scrollIntoView({ block: "nearest" });
    this.inputTarget.setAttribute("aria-activedescendant", activeEl?.id || "");
  }

  #handleEnterKey(items) {
    const target = items[this.#activeIndex] ?? items[0];
    if (!target) return;
    if (target.dataset.paletteAction === "shortcuts") {
      this.close();
      document.getElementById("sc-shortcuts-dialog")?.showModal();
    } else {
      this.close();
      if (target.target === "_blank") {
        window.open(target.dataset.href, "_blank", "noopener,noreferrer");
      } else {
        this.#navigate(target.dataset.href);
      }
    }
  }

  closeOnItemClick() {
    this.close();
  }

  openShortcutsDialog() {
    this.close();
    document.getElementById("sc-shortcuts-dialog")?.showModal();
  }

  showShortcuts() {
    const STATIC = [
      { label: this.openPaletteValue, shortcut: "ctrl k" },
      {
        label:
          this.itemsValue.find((i) => i.action === "shortcuts")?.label ||
          this.keyboardShortcutsValue,
        shortcut: "?",
      },
    ];
    const navShortcuts = this.itemsValue
      .filter((i) => i.shortcut && !i.action)
      .map((i) => ({ label: i.label, shortcut: i.shortcut }));

    this.listTarget.innerHTML = "";
    this.#activeIndex = -1;

    const heading = document.createElement("p");
    heading.className = "cmd-palette-empty";
    heading.style.cssText =
      "padding: 10px 0 6px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em;";
    heading.textContent = this.keyboardShortcutsValue;
    this.listTarget.appendChild(heading);

    [...STATIC, ...navShortcuts].forEach(({ label, shortcut }) => {
      const row = document.createElement("div");
      row.className = "cmd-palette-item";
      row.style.cursor = "default";

      const icon = document.createElement("i");
      icon.className = "fa-regular fa-keyboard cmd-palette-icon";

      const labelEl = document.createElement("span");
      labelEl.className = "cmd-palette-label";
      labelEl.textContent = label;

      const hint = document.createElement("span");
      hint.className = "cmd-palette-shortcut";
      hint.textContent = shortcut;

      row.appendChild(icon);
      row.appendChild(labelEl);
      row.appendChild(hint);
      this.listTarget.appendChild(row);
    });
  }

  // ── DOM rendering ────────────────────────────────────────────────────────

  #renderItems(items) {
    this.listTarget.innerHTML = "";

    if (!items.length) {
      const empty = document.createElement("p");
      empty.className = "cmd-palette-empty";
      empty.setAttribute("role", "status");
      empty.textContent = this.noResultsValue;
      this.listTarget.appendChild(empty);
      return;
    }

    items.forEach((item, index) => {
      let el;
      if (item.action === "shortcuts") {
        el = document.createElement("button");
        el.type = "button";
        el.dataset.paletteAction = "shortcuts";
        el.dataset.action = "click->command-palette#openShortcutsDialog";
        el.style.width = "100%";
        el.style.textAlign = "left";
      } else {
        el = document.createElement("a");
        el.href = item.href;
        el.dataset.href = item.href;
        el.dataset.action = "click->command-palette#closeOnItemClick";
      }
      el.className = "cmd-palette-item";
      el.setAttribute("role", "option");
      el.setAttribute("aria-selected", "false");
      el.id = "cmd-palette-option-" + index;

      const icon = document.createElement("i");
      icon.className = `${item.icon} cmd-palette-icon`;

      const label = document.createElement("span");
      label.className = "cmd-palette-label";
      label.textContent = item.label;

      const hint = document.createElement("span");
      hint.className = "cmd-palette-shortcut";
      hint.textContent = item.shortcut || "";

      el.appendChild(icon);
      el.appendChild(label);
      el.appendChild(hint);
      this.listTarget.appendChild(el);
    });
  }

  // ── Navigation ───────────────────────────────────────────────────────────

  #navigate(href) {
    if (!isSafeHref(href)) return;

    if (window.Turbo) {
      Turbo.visit(href);
    } else {
      window.location.href = href;
    }
  }
}
